/**
 * FEATURE: BATTLE_MODE
 * 
 * Battle Mode Modal - Toont lijst van openstaande battles en knop om nieuwe battle aan te maken
 * 
 * Om terug te draaien:
 * - Verwijder dit bestand
 * - Verwijder import en gebruik in WordHit.jsx
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import Parse from "../parseClient";
import * as amplitude from "@amplitude/unified";
import "./BattleModeModal.css";
import { isBattleModeEnabled } from "../utils/featureFlags";
import { share as shareUtil } from "../utils/share";

function BattleModeModal({ onClose, onSelectBattle, refreshRef, langString, onRequireLogin }) {
  const [battleGames, setBattleGames] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [showCreatePopup, setShowCreatePopup] = useState(false);
  const [opponentSearch, setOpponentSearch] = useState("");
  const [battleLanguage, setBattleLanguage] = useState(langString === "nl" ? "nl" : "en");
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [decliningBattleId, setDecliningBattleId] = useState(null);
  const [isUnauthenticated, setIsUnauthenticated] = useState(false);
  const [isMatchingRandom, setIsMatchingRandom] = useState(false);
  const [randomMatchError, setRandomMatchError] = useState(null);

  // Check if Battle Mode is enabled
  if (!isBattleModeEnabled()) {
    return null;
  }

  const loadBattleGames = useCallback(async (silent = false) => {
    try {
      if (!silent) {
        setIsLoading(true);
        setError(null);
      }

      const currentUser = Parse.User.current();
      if (!currentUser) {
        setIsUnauthenticated(true);
        if (!silent) {
          setError(
            langString === "nl"
              ? "Je moet ingelogd zijn om battles te kunnen spelen."
              : "You must be logged in to play battles."
          );
          setIsLoading(false);
        }
        return;
      }

      setIsUnauthenticated(false);

      const result = await Parse.Cloud.run("getUserBattles");
      if (result && result.success && result.battles) {
        setBattleGames(result.battles);
      } else if (!silent) {
        setBattleGames([]);
      }
    } catch (err) {
      if (!silent) {
        console.error("[BattleMode] Error loading battle games:", err);
        setError(
          langString === "nl"
            ? `Kon battles niet laden: ${err.message || "Onbekende fout"}`
            : `Could not load battles: ${err.message || "Unknown error"}`
        );
      }
    } finally {
      if (!silent) {
        setIsLoading(false);
      }
    }
  }, [langString]);

  useEffect(() => {
    loadBattleGames(false);
  }, [loadBattleGames]);

  // Expose refresh so parent can call when e.g. user closes WordHitBattle (list updates immediately, green → not green)
  useEffect(() => {
    if (refreshRef) {
      refreshRef.current = () => loadBattleGames(true);
      return () => { refreshRef.current = null; };
    }
  }, [refreshRef, loadBattleGames]);

  // Refresh battle list every 5s so "your turn" (green) updates when turn changes
  useEffect(() => {
    const interval = setInterval(() => {
      loadBattleGames(true);
    }, 5000);
    return () => clearInterval(interval);
  }, [loadBattleGames]);

  const handleCreateBattle = () => {
    // Toon popup voor het invoeren van opponent naam/email
    setShowCreatePopup(true);
    setOpponentSearch("");
    setSearchError(null);
    setBattleLanguage(langString === "nl" ? "nl" : "en");
  };

  const handleRandomBattle = async () => {
    try {
      setIsMatchingRandom(true);
      setRandomMatchError(null);

      const currentUser = Parse.User.current();
      if (!currentUser) {
        if (onRequireLogin) {
          onRequireLogin();
          return;
        }
        setRandomMatchError(
          langString === "nl"
            ? "Je moet ingelogd zijn om een willekeurige battle te spelen."
            : "You must be logged in to play a random battle."
        );
        return;
      }

      const lang = (battleLanguage === "nl" || battleLanguage === "en")
        ? battleLanguage
        : (langString === "nl" ? "nl" : "en");
      const result = await Parse.Cloud.run("joinRandomBattle", { language: lang });

      if (!result || !result.success || !result.userBattleId) {
        setRandomMatchError(
          langString === "nl"
            ? "Kon geen willekeurige battle starten. Probeer het later opnieuw."
            : "Could not start a random battle. Please try again later."
        );
        return;
      }

      const battleId = result.userBattleId;

      // Random battle started successfully: close the create popup
      setShowCreatePopup(false);
      setOpponentSearch("");
      setSearchError(null);
      setRandomMatchError(null);

      // Haal battles opnieuw op zodat lijst én battleInfo up-to-date zijn
      const battlesResult = await Parse.Cloud.run("getUserBattles");
      if (battlesResult && battlesResult.success && Array.isArray(battlesResult.battles)) {
        setBattleGames(battlesResult.battles);

        const found = battlesResult.battles.find(
          (b) => b.id === battleId || b.objectId === battleId
        );

        if (found && onSelectBattle) {
          onSelectBattle(found.battleGameId, found.idNumber, found);
        }
      } else {
        // Fallback: alleen battleId doorgeven, WordHitBattle haalt zelf de rest op
        if (onSelectBattle) {
          onSelectBattle(battleId, null, null);
        }
      }
    } catch (err) {
      console.error("[BattleMode] Error starting random battle:", err);
      setRandomMatchError(
        langString === "nl"
          ? (err.message || "Er ging iets mis bij het starten van een willekeurige battle.")
          : (err.message || "Something went wrong while starting a random battle.")
      );
    } finally {
      setIsMatchingRandom(false);
    }
  };

  const handleSearchOpponent = async () => {
    if (!opponentSearch.trim()) {
      setSearchError(
        langString === "nl"
          ? "Voer een naam of emailadres in"
          : "Please enter a name or email address"
      );
      return;
    }

    try {
      setIsSearching(true);
      setSearchError(null);

      // Zoek user op naam of email
      const searchResult = await Parse.Cloud.run("searchUser", {
        searchTerm: opponentSearch.trim(),
      });

      if (!searchResult.success || !searchResult.user) {
        setSearchError(
          langString === "nl"
            ? "Gebruiker niet gevonden. Controleer de naam of het emailadres."
            : "User not found. Please check the name or email address."
        );
        return;
      }

      // Maak battle aan met gevonden opponent en gekozen taal
      const battleResult = await Parse.Cloud.run("startBattle", {
        opponentId: searchResult.user.id,
        language: battleLanguage,
      });

      if (battleResult.success) {
        try {
          amplitude.track("battle_created", {
            opponent_id: searchResult.user.id,
            language: battleLanguage,
            user_battle_id: battleResult.userBattleId || undefined,
          });
        } catch (amplitudeError) {
          console.warn("Failed to track battle_created:", amplitudeError);
        }
        // Herlaad battles lijst
        await loadBattleGames();
        
        // Sluit popup
        setShowCreatePopup(false);
        setOpponentSearch("");
        
        // Optioneel: open de nieuwe battle direct
        // if (onSelectBattle && battleResult.userBattleId) {
        //   onSelectBattle(battleResult.userBattleId, null, null);
        // }
      } else {
        setSearchError(
          langString === "nl"
            ? "Kon battle niet aanmaken. Probeer het later opnieuw."
            : "Could not create battle. Please try again later."
        );
      }
    } catch (err) {
      console.error("[BattleMode] Error creating battle:", err);
      setSearchError(
        langString === "nl"
          ? err.message || "Kon battle niet aanmaken. Probeer het later opnieuw."
          : err.message || "Could not create battle. Please try again later."
      );
    } finally {
      setIsSearching(false);
    }
  };

  const handleCloseCreatePopup = () => {
    setShowCreatePopup(false);
    setOpponentSearch("");
    setSearchError(null);
  };

  const handleInviteShare = async () => {
    const inviteUrl = langString === "nl" ? "https://wordhit.app/nederlands" : "https://wordhit.app";
    const currentUser = Parse.User.current();
    const myName = currentUser?.get("name") || currentUser?.get("username") || "";
    const nameLine = myName
      ? (langString === "nl" ? `Mijn WordHit-naam is ${myName}.` : `My WordHit name is ${myName}.`)
      : "";
    const shareText = langString === "nl"
      ? (nameLine
          ? `Speel WordHit Battle tegen mij! ${nameLine}\n${inviteUrl}`
          : `Speel WordHit Battle tegen mij!\n${inviteUrl}`)
      : (nameLine
          ? `Play WordHit Battle against me! ${nameLine}\n${inviteUrl}`
          : `Play WordHit Battle against me!\n${inviteUrl}`);
    const shareTitle = langString === "nl" ? "WordHit Battle" : "WordHit Battle";
    try {
      await shareUtil({ title: shareTitle, text: shareText });
    } catch (err) {
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(shareText);
          const msg = langString === "nl"
            ? "Tekst gekopieerd naar klembord. Deel hem via je favoriete app."
            : "Text copied to clipboard. Share it via your preferred app.";
          alert(msg);
        } else {
          throw new Error("Clipboard not available");
        }
      } catch (clipboardErr) {
        const msg = langString === "nl"
          ? "Delen niet beschikbaar in deze browser. Deel de link handmatig: " + inviteUrl
          : "Sharing not available in this browser. Share the link manually: " + inviteUrl;
        alert(msg);
      }
    }
  };

  const handleDeclineBattle = async (userBattleId, e) => {
    e.stopPropagation(); // Prevent battle click when clicking decline button
    
    if (!userBattleId) {
      console.error("[BattleMode] No userBattleId provided for decline");
      return;
    }

    // Confirm before declining
    const confirmMessage = langString === "nl" 
      ? "Weet je zeker dat je deze battle wilt weigeren?"
      : "Are you sure you want to decline this battle?";
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setDecliningBattleId(userBattleId);
      setError(null);

      const result = await Parse.Cloud.run("declineBattle", {
        userBattleId: userBattleId,
      });

      if (result.success) {
        // Reload battles list
        await loadBattleGames();
      } else {
        setError(
          langString === "nl"
            ? "Kon battle niet weigeren. Probeer het later opnieuw."
            : "Could not decline battle. Please try again later."
        );
      }
    } catch (err) {
      console.error("[BattleMode] Error declining battle:", err);
      setError(
        langString === "nl"
          ? err.message || "Kon battle niet weigeren. Probeer het later opnieuw."
          : err.message || "Could not decline battle. Please try again later."
      );
    } finally {
      setDecliningBattleId(null);
    }
  };

  const handleBattleClick = (battleGameId, idNumber, battleInfo) => {
    console.log("[BattleMode] Battle clicked:", { battleGameId, idNumber, battleInfo });
    if (!battleGameId) {
      console.error("[BattleMode] No battleGameId provided!");
      setError(
        langString === "nl"
          ? "Battle game niet gevonden. Probeer het later opnieuw."
          : "Battle game not found. Please try again later."
      );
      return;
    }
    if (onSelectBattle) {
      // Pass battleGameId and battleInfo (with players) to WordHitBattle
      onSelectBattle(battleGameId, idNumber, battleInfo);
    }
  };

  return (
    <>
      <div 
        className="battle-mode-overlay" 
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      />
      <div className="battle-mode-modal">
        <div className="battle-mode-header">
          <button 
            className="battle-mode-close" 
            onClick={onClose}
            aria-label={langString === "nl" ? "Sluiten" : "Close"}
          >
            ✕
          </button>
          <div className="battle-mode-header-logo-wrap">
            <img src={`${process.env.PUBLIC_URL || ""}/battle-logo.png`} alt="Battle Mode" className="battle-mode-logo" />
          </div>
        </div>

        <div className={`battle-mode-content${isLoading ? " battle-mode-content-loading" : ""}`}>
          {isLoading ? (
            <div className="battle-mode-loading">
              <div className="battle-mode-spinner" aria-hidden="true" />
            </div>
          ) : isUnauthenticated ? (
            <div className="battle-mode-unauthenticated">
              {/* Battle rules explanation - also visible when not logged in */}
              <div className="battle-mode-rules">
                {langString === "nl" 
                  ? "Speel WordHit 1-op-1 tegen een andere speler!\n\nElke beurt mag je 2 acties doen: een letter klikken of een woord raden. Wie als eerste 10 woorden vindt, wint de battle."
                  : "Play WordHit 1-on-1 against another player!\n\nEach turn you can do 2 actions: click a letter or guess a word. The first to find 10 words wins the battle."}
              </div>
              {onRequireLogin && (
                <button
                  className="battle-mode-login-button"
                  onClick={() => {
                    onRequireLogin();
                  }}
                >
                  {langString === "nl"
                    ? "Inloggen of account aanmaken"
                    : "Sign in or create account"}
                </button>
              )}
              {error && <div className="battle-mode-error">{error}</div>}
            </div>
          ) : error ? (
            <div className="battle-mode-error">{error}</div>
          ) : (
            <>
              {/* Battle rules explanation */}
              <div className="battle-mode-rules">
                {langString === "nl" 
                  ? "Speel WordHit 1-op-1 tegen een andere speler!\n\nElke beurt mag je 2 acties doen: een letter klikken of een woord raden. Wie als eerste 10 woorden vindt, wint de battle."
                  : "Play WordHit 1-on-1 against another player!\n\nEach turn you can do 2 actions: click a letter or guess a word. The first to find 10 words wins the battle."}
              </div>
              
              <button
                onClick={handleCreateBattle}
                disabled={isCreating}
                className="battle-mode-create-button"
              >
                {langString === "nl" ? "Nieuwe Battle" : "New Battle"}
              </button>

              {(() => {
                const currentUser = Parse.User.current();
                const openBattles = battleGames.filter((g) => g.status !== "finished" && !g.battleWinnerId);
                const finishedBattles = battleGames
                  .filter((g) => g.status === "finished" || g.battleWinnerId)
                  .sort((a, b) => {
                    const aAt = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
                    const bAt = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
                    return bAt - aAt;
                  });
                const hasAnyBattles = openBattles.length > 0 || finishedBattles.length > 0;

                if (!hasAnyBattles) {
                  return null;
                }

                const renderBattleItem = (game, isFinished) => {
                  const isOpponent = currentUser && game.opponent &&
                    (currentUser.id === game.opponent.id || String(currentUser.id) === String(game.opponent.id));
                  const isMyTurn = currentUser && game.turnPlayerId &&
                    (currentUser.id === game.turnPlayerId || String(currentUser.id) === String(game.turnPlayerId));
                  const turnClass = isFinished ? "battle-mode-game-item-finished" : (isMyTurn ? "battle-mode-game-item-my-turn" : "battle-mode-game-item-opponent-turn");
                  const iWon = isFinished && game.battleWinnerId && currentUser && (currentUser.id === game.battleWinnerId || String(currentUser.id) === String(game.battleWinnerId));

                  return (
                    <li
                      key={game.id}
                      className={`battle-mode-game-item ${turnClass}`}
                      onClick={() => handleBattleClick(game.battleGameId, game.idNumber, game)}
                    >
                      {!isFinished && (
                        <button
                          className="battle-mode-decline-button"
                          onClick={(e) => handleDeclineBattle(game.id, e)}
                          disabled={decliningBattleId === game.id}
                          aria-label={langString === "nl" ? "Battle weigeren" : "Decline battle"}
                          title={langString === "nl" ? "Battle weigeren" : "Decline battle"}
                          style={{
                            position: "absolute",
                            top: "4px",
                            right: "4px",
                            background: "transparent",
                            color: "#000",
                            border: "none",
                            borderRadius: "4px",
                            padding: "4px",
                            fontSize: "1.05rem",
                            fontWeight: "normal",
                            cursor: "pointer",
                            zIndex: 1000,
                            minWidth: "24px",
                            minHeight: "24px",
                            width: "24px",
                            height: "24px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxShadow: "none",
                            opacity: isOpponent ? 1 : 0.3,
                          }}
                        >
                          {decliningBattleId === game.id ? "..." : "✕"}
                        </button>
                      )}
                      <div className="battle-mode-game-info">
                        <span className="battle-mode-game-id">
                          {game.opponent
                            ? `${langString === "nl" ? "Tegen" : "Vs"} ${game.opponent.name}`
                            : (
                              langString === "nl"
                                ? "Tegen iemand die we nog zoeken"
                                : "Vs someone we are still finding"
                            )}
                        </span>
                        {isFinished && (
                          <span className="battle-mode-game-finished-label">
                            {langString === "nl" ? "Afgelopen" : "Finished"}
                            {iWon ? ` · ${langString === "nl" ? "Jij wint" : "You win"}` : ` · ${langString === "nl" ? "Tegenstander wint" : "Opponent wins"}`}
                          </span>
                        )}
                        {game.totalDiscoveredWords && currentUser && (
                          <span className="battle-mode-game-total" style={{ fontSize: "1.35rem", fontWeight: "bold", color: isFinished ? "var(--text-dark, #1a1a1a)" : "var(--sf-bg, #fefbef)", display: "block", marginTop: "4px" }}>
                            {(() => {
                              const currentUserId = currentUser.id;
                              const opponentId = game.opponent?.id;
                              const myTotal = game.totalDiscoveredWords[currentUserId] || 0;
                              const opponentTotal = opponentId ? (game.totalDiscoveredWords[opponentId] || 0) : 0;
                              return `${myTotal} - ${opponentTotal}`;
                            })()}
                          </span>
                        )}
                        {!game.battleGameId && (
                          <span style={{ fontSize: "0.9rem", color: "#e62731", display: "block", marginTop: "4px" }}>
                            {langString === "nl" ? "Geen battle game" : "No battle game"}
                          </span>
                        )}
                      </div>
                    </li>
                  );
                };

                return (
                  <>
                    <div className="battle-mode-divider" />
                    {openBattles.length > 0 && (
                      <div className="battle-mode-list battle-mode-list-open">
                        <h3>
                          {langString === "nl" ? "Openstaande Battles" : "Open Battles"}
                        </h3>
                        <ul className="battle-mode-games">
                          {openBattles.map((game) => renderBattleItem(game, false))}
                        </ul>
                      </div>
                    )}
                    {finishedBattles.length > 0 && (
                      <div className="battle-mode-list battle-mode-list-finished">
                        <h3>
                          {langString === "nl" ? "Afgelopen battles" : "Finished battles"}
                        </h3>
                        <ul className="battle-mode-games">
                          {finishedBattles.map((game) => renderBattleItem(game, true))}
                        </ul>
                      </div>
                    )}
                  </>
                );
              })()}
            </>
          )}
        </div>
      </div>

      {/* Create Battle Popup */}
      {showCreatePopup && (
        <>
          <div 
            className="battle-mode-overlay" 
            onClick={handleCloseCreatePopup}
            style={{ zIndex: 10000 }}
          />
          <div className="battle-mode-create-popup">
            <div className="battle-mode-create-popup-header">
              <h3>{langString === "nl" ? "Nieuwe Battle" : "New Battle"}</h3>
              <button 
                className="battle-mode-close" 
                onClick={handleCloseCreatePopup}
                aria-label={langString === "nl" ? "Sluiten" : "Close"}
              >
                ✕
              </button>
            </div>
            <div className="battle-mode-create-popup-content">
              <div className="battle-mode-create-popup-start-wrap" style={{ marginBottom: "16px" }}>
                <button
                  type="button"
                  onClick={handleRandomBattle}
                  disabled={isMatchingRandom}
                  className="battle-mode-create-popup-ok"
                >
                  {isMatchingRandom
                    ? (langString === "nl" ? "Zoeken naar willekeurige tegenstander..." : "Searching for random opponent...")
                    : (langString === "nl" ? "Willekeurige tegenstander" : "Random opponent")}
                </button>
              </div>
              {randomMatchError && (
                <div className="battle-mode-error" style={{ marginBottom: "16px" }}>
                  {randomMatchError}
                </div>
              )}
              <div
                style={{
                  textAlign: "center",
                  fontSize: "1.4rem",
                  fontWeight: "bold",
                  marginTop: "40px",
                  marginBottom: "44px",
                }}
              >
                {langString === "nl" ? "OF" : "OR"}
              </div>
              <input
                id="opponent-search"
                type="text"
                value={opponentSearch}
                onChange={(e) => setOpponentSearch(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter" && !isSearching) {
                    handleSearchOpponent();
                  }
                }}
                placeholder={langString === "nl" 
                  ? "Voer naam of email van een speler in..." 
                  : "Enter a player's name or email..."}
                className="battle-mode-create-input"
                disabled={isSearching}
              />
              <select
                id="battle-language"
                value={battleLanguage}
                onChange={(e) => setBattleLanguage(e.target.value)}
                className="battle-mode-create-input"
                disabled={isSearching}
                style={{ marginTop: "6px", cursor: "pointer" }}
              >
                <option value="nl">{langString === "nl" ? "Taal: Nederlands" : "Language: Dutch"}</option>
                <option value="en">{langString === "nl" ? "Taal: Engels" : "Language: English"}</option>
              </select>
              {searchError && (
                <div className="battle-mode-error" style={{ marginTop: "12px" }}>
                  {searchError}
                </div>
              )}
              {opponentSearch.trim() && (
                <div className="battle-mode-create-popup-start-wrap">
                  <button
                    onClick={handleSearchOpponent}
                    className="battle-mode-create-popup-ok"
                    disabled={isSearching}
                  >
                    {isSearching
                      ? (langString === "nl" ? "Zoeken..." : "Searching...")
                      : (langString === "nl" ? "Battle beginnen" : "Start Battle")}
                  </button>
                </div>
              )}
              <div className="battle-mode-create-popup-divider" />
              <div className="battle-mode-create-popup-invite-wrap">
                <button
                  type="button"
                  onClick={handleInviteShare}
                  className="battle-mode-invite-button"
                  aria-label={langString === "nl" ? "Anderen uitnodigen" : "Invite others"}
                >
                  {langString === "nl" ? "Anderen uitnodigen" : "Invite others"}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default BattleModeModal;
